import { createContext, useContext } from 'react'

// FormField / CompactFormField / InlineFormField が、中の入力部品へエラー状態を配るための context。
// 入力部品ごとに aria-invalid や aria-describedby を手書きさせないのが目的（書き忘れると赤枠にならない）

export type FormFieldContextValue = {
  /** 入力欄に付ける id。label が入力を包む FormField では undefined（複数の入力が入っても id が重複しないように） */
  controlId: string | undefined
  invalid: boolean
  /** エラー文・hint の id を空白区切りで連結したもの */
  describedBy: string | undefined
}

export const FormFieldContext = createContext<FormFieldContextValue | null>(null)

type ControlAriaProps = {
  id?: string
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling'
  'aria-describedby'?: string
}

/** 空白区切りの id 列を連結する。重複と空は除く */
export function mergeDescribedBy(...values: Array<string | undefined>): string | undefined {
  const ids = values.flatMap((value) => (value === undefined ? [] : value.split(/\s+/).filter((id) => id !== '')))
  const unique = [...new Set(ids)]
  return unique.length === 0 ? undefined : unique.join(' ')
}

/**
 * 入力部品が自分の props に重ねて展開する id / aria 属性を返す。
 * 呼び出し側が明示した値を優先し、aria-describedby だけは両方を残す
 */
export function useFormControlProps(explicit: ControlAriaProps): ControlAriaProps {
  const field = useContext(FormFieldContext)
  if (field === null) {
    return {
      id: explicit.id,
      'aria-invalid': explicit['aria-invalid'],
      'aria-describedby': explicit['aria-describedby'],
    }
  }
  return {
    id: explicit.id ?? field.controlId,
    // invalid でないときは付けない（aria-invalid="false" を撒かない）
    'aria-invalid': explicit['aria-invalid'] ?? (field.invalid ? true : undefined),
    'aria-describedby': mergeDescribedBy(explicit['aria-describedby'], field.describedBy),
  }
}
